
import { defineComponent, onMounted, reactive, ref, toRefs } from 'vue'
import SearchForm from '@/components/form/search/SearchForm.vue'
import Container from '@/components/form/container/Container.vue'
import { useI18n } from 'vue-i18n'
import { createModel } from '@/utils/component'
import {
  PaymentSlipSumBySupplierModel,
  PaymentSlipModel,
  PaymentSlipPageParamModel, PaymentSlipGroupBySupplierModel
} from '@/model/paymentSlip/paymentSlipModel'
import { formatDate, formatThousands } from '@/utils'
import {
  exportPaymentSlip,
  exportPaymentSlipSumBySupplier,
  getPaymentSlipGroupBySupplierPageList,
  getPaymentSlipPageList, getPaymentSlipSumBySupplierPageList
} from '@/apis/paymentSlip/paymentSlip'
import SimpleTable from '@/components/table/simple/SimpleTable.vue'
import dayjs from 'dayjs'
import { CompanyListParamModel } from '@/model/master-data/companyModel'
import { getCompanyAllList } from '@/apis/main-data/company'
import { SpanMethodProps } from '@/components/table/simple/TableTypes'
import { ElMessage } from 'element-plus'

export default defineComponent({
  name: 'PlannedPaymentReportsPageList',
  components: {
    SearchForm,
    SimpleTable,
    Container
  },
  setup() {
    const { t } = useI18n()
    const tableRef = ref()
    const paramModelRef = ref<PaymentSlipPageParamModel | null>(null)

    enum ReportDisplayTypes {
      SUPPLIER_SUM_DISPLAY,
      SINGLE_ROW_DISPLAY,
      SUPPLIER_SUM_AND_SINGLE_ROW_DISPLAY
    }

    const searchStruct = [
      {
        prop: 'companyCode',
        label: t('company.companyCode'),
        component: 'el-select',
        type: 'text',
        model: '',
        filterable: true,
        loading: false,
        options: [] as any
      },
      { prop: 'payee', label: t('pay.payee'), component: 'el-input', type: 'text', model: '' },
      {
        prop: 'dueDate',
        label: t('order.cutOffDate'),
        component: 'el-date-picker',
        type: 'date',
        model: '',
        suffix: '前'
      },
      { prop: 'currency', label: t('pay.currency'), component: 'el-input', type: 'text', model: '' },
      {
        prop: 'isForeignCurrency',
        label: t('pay.foreignExchangeOrExternal'),
        component: 'el-select',
        type: 'boolean',
        model: null,
        options: [
          { label: t('common.all'), value: null },
          { label: t('common.yes'), value: true },
          { label: t('common.no'), value: false }
        ]
      },
      { prop: 'comment', label: t('approvalForm.comment'), component: 'el-input', type: 'text', model: '' }
    ]
    const supplierSumTableStruct = [
      { prop: 'id', label: t('pay.id') },
      { prop: 'companyCode', label: t('company.companyCode') },
      { prop: 'payee', label: t('pay.payee') },
      { prop: 'amount', label: t('pay.amount'), formatter: (row: any) => formatThousands(row.amount), align: 'right' },
      { prop: 'currency', label: t('pay.currency') }
    ]
    const singleRowTableStruct = [
      { prop: 'id', label: t('pay.id') },
      { prop: 'companyCode', label: t('company.companyCode') },
      { prop: 'payee', label: t('pay.payee') },
      { prop: 'reference', label: t('pay.reference') },
      { prop: 'amount', label: t('pay.amount'), formatter: (row: any) => formatThousands(row.amount), align: 'right' },
      { prop: 'currency', label: t('pay.currency') },
      { prop: 'dueDate', label: t('order.cutOffDate'), formatter: (row: any) => formatDate(row.dueDate, 'yyyy-MM-dd') },
      { prop: 'comments', label: t('pay.comments') },
      {
        prop: 'paymentDate',
        label: t('pay.paymentDate'),
        formatter: (row: any) => formatDate(row.paymentDate, 'yyyy-MM-dd')
      },
      { prop: 'purpose', label: t('pay.purposeView'), width: 170 }
    ]
    const groupTableStruct = [
      { prop: 'id', label: t('pay.id') },
      { prop: 'companyCode', label: t('company.companyCode') },
      { prop: 'payee', label: t('pay.payee') },
      { prop: 'reference', label: t('pay.reference') },
      { prop: 'amount', label: t('pay.amount'), formatter: (row: any) => formatThousands(row.amount), align: 'right' },
      { prop: 'currency', label: t('pay.currency') },
      { prop: 'dueDate', label: t('order.cutOffDate'), formatter: (row: any) => formatDate(row.dueDate, 'yyyy-MM-dd') },
      { prop: 'comments', label: t('pay.comments') },
      {
        prop: 'paymentDate',
        label: t('pay.paymentDate'),
        formatter: (row: any) => formatDate(row.paymentDate, 'yyyy-MM-dd')
      },
      { prop: 'purpose', label: t('pay.purposeView'), width: 170 }
    ]
    const dataMap = reactive({
      searchFormStruct: searchStruct,
      searchFormModel: createModel(searchStruct) as PaymentSlipPageParamModel,
      supplierSumColumnStruct: supplierSumTableStruct,
      supplierSumTableData: Array<PaymentSlipSumBySupplierModel>(),
      singleRowColumnStruct: singleRowTableStruct,
      singleRowTableData: Array<PaymentSlipModel>(),
      groupColumnStruct: groupTableStruct,
      groupColumnTableData: Array<PaymentSlipGroupBySupplierModel>(),
      listLoading: true,
      reportDisplayTypes: ReportDisplayTypes.SUPPLIER_SUM_DISPLAY,
      limit: 10,
      total: 0,
      page: 1,
      handleCurrentChange(page?: any) {
        dataMap.getPageList(page)
      },
      handleSizeChange(val: any) {
        dataMap.getPageList(null, val)
      },
      arraySpanMethod({ row, columnIndex }: SpanMethodProps) {
        if (dataMap.reportDisplayTypes === ReportDisplayTypes.SUPPLIER_SUM_AND_SINGLE_ROW_DISPLAY) {
          if (row.isSumRow) {
            if (columnIndex === 2) {
              return [1, 2]
            } else if (columnIndex === 3) {
              return [0, 0]
            } else if (columnIndex === 6) {
              return [1, 4]
            }
          }
        }
      },
      async getPageList(page?: any, limit?: any) {
        if (page) {
          dataMap.page = page
        }
        if (limit) {
          dataMap.limit = limit
        }
        dataMap.listLoading = true
        let dueDate = ''
        if (dataMap.searchFormModel.dueDate) {
          dueDate = dayjs(dataMap.searchFormModel.dueDate).endOf('day').format('YYYY-MM-DD HH:mm:ss SSS')
        }
        const params: PaymentSlipPageParamModel = {
          companyCode: dataMap.searchFormModel.companyCode,
          payee: dataMap.searchFormModel.payee,
          dueDate: dueDate,
          currency: dataMap.searchFormModel.currency,
          isForeignCurrency: dataMap.searchFormModel.isForeignCurrency,
          comment: dataMap.searchFormModel.comment,
          pageCurrent: dataMap.page,
          pageSize: dataMap.limit
        }
        if (dataMap.reportDisplayTypes === ReportDisplayTypes.SUPPLIER_SUM_DISPLAY) {
          const data = await getPaymentSlipSumBySupplierPageList(params)
          if (data && data.data && data.data.list.length > 0) {
            dataMap.supplierSumTableData = data.data.list
          } else {
            dataMap.supplierSumTableData = []
          }
          dataMap.total = data?.data.totalCount ?? 0
        } else if (dataMap.reportDisplayTypes === ReportDisplayTypes.SINGLE_ROW_DISPLAY) {
          const data = await getPaymentSlipPageList(params)
          if (data && data.data && data.data.list.length > 0) {
            dataMap.singleRowTableData = data.data.list
          } else {
            dataMap.singleRowTableData = []
          }
          dataMap.total = data?.data.totalCount ?? 0
        } else if (dataMap.reportDisplayTypes === ReportDisplayTypes.SUPPLIER_SUM_AND_SINGLE_ROW_DISPLAY) {
          const data = await getPaymentSlipGroupBySupplierPageList(params)
          if (data && data.data && data.data.list.length > 0) {
            dataMap.groupColumnTableData = data.data.list
          } else {
            dataMap.groupColumnTableData = []
          }
          dataMap.total = data?.data.totalCount ?? 0
        } else {
          dataMap.page = 1
          dataMap.total = 0
          dataMap.supplierSumTableData = []
          dataMap.singleRowTableData = []
        }
        paramModelRef.value = params
        // Just to simulate the time of the request
        setTimeout(() => {
          dataMap.listLoading = false
        }, 0.5 * 1000)
      },
      submitSearch() {
        dataMap.page = 1
        dataMap.getPageList()
      },
      resetForm() {
        console.log('reset')
      },
      supplierSumDisplayClick() {
        if (dataMap.reportDisplayTypes === ReportDisplayTypes.SUPPLIER_SUM_DISPLAY) {
          return
        }
        dataMap.reportDisplayTypes = ReportDisplayTypes.SUPPLIER_SUM_DISPLAY
        dataMap.getPageList(1, 10)
      },
      singleRowDisplayClick() {
        if (dataMap.reportDisplayTypes === ReportDisplayTypes.SINGLE_ROW_DISPLAY) {
          return
        }
        dataMap.reportDisplayTypes = ReportDisplayTypes.SINGLE_ROW_DISPLAY
        dataMap.getPageList(1, 10)
      },
      supplierSumAndSingleRowDisplayClick() {
        if (dataMap.reportDisplayTypes === ReportDisplayTypes.SUPPLIER_SUM_AND_SINGLE_ROW_DISPLAY) {
          return
        }
        dataMap.reportDisplayTypes = ReportDisplayTypes.SUPPLIER_SUM_AND_SINGLE_ROW_DISPLAY
        dataMap.getPageList(1, 10)
      },
      async handleExport() {
        if (!paramModelRef.value) {
          alert('请先进行检索')
          return
        }
        const params: PaymentSlipPageParamModel = {
          companyCode: paramModelRef.value.companyCode,
          payee: paramModelRef.value.payee,
          dueDate: paramModelRef.value.dueDate,
          currency: paramModelRef.value.currency,
          isForeignCurrency: paramModelRef.value.isForeignCurrency,
          comment: paramModelRef.value.comment,
          pageCurrent: paramModelRef.value.pageCurrent,
          pageSize: paramModelRef.value.pageSize
        }
        let urlBlob = null
        if (dataMap.reportDisplayTypes === ReportDisplayTypes.SUPPLIER_SUM_DISPLAY) {
          urlBlob = await exportPaymentSlipSumBySupplier(params)
        } else if (dataMap.reportDisplayTypes === ReportDisplayTypes.SINGLE_ROW_DISPLAY) {
          urlBlob = await exportPaymentSlip(params)
        } else if (dataMap.reportDisplayTypes === ReportDisplayTypes.SUPPLIER_SUM_AND_SINGLE_ROW_DISPLAY) {
          urlBlob = await exportPaymentSlip(params)
        }
        if (urlBlob) {
          const link = document.createElement('a')
          link.href = urlBlob
          link.download = '计划支付单报表.xlsx'
          link.click()
          window.URL.revokeObjectURL(urlBlob)
        } else {
          ElMessage({
            message: '获取excel失败',
            type: 'error',
            duration: 2000
          })
        }
      }
    })
    const tableRowClassName = ({ row }: { row: PaymentSlipModel }) => {
      if (row.paymentDate !== null) {
        // 复制 Date 对象，避免修改原对象
        const paymentDate = new Date(row.paymentDate).setHours(0, 0, 0, 0)
        const nowDateTime = new Date().setHours(0, 0, 0, 0)
        if (paymentDate <= nowDateTime) {
          return 'warning-row'
        }
      }
      return ''
    }
    const groupTableRowClassName = ({ row }: { row: PaymentSlipGroupBySupplierModel }) => {
      if (row.isSumRow) {
        return 'summary-row'
      } else if (row.paymentDate) {
        // 复制 Date 对象，避免修改原对象
        const paymentDate = new Date(row.paymentDate).setHours(0, 0, 0, 0)
        const nowDateTime = new Date().setHours(0, 0, 0, 0)
        if (paymentDate <= nowDateTime) {
          return 'warning-row'
        }
      }
      return ''
    }
    const getCompanyList = async() => {
      dataMap.searchFormStruct[0].loading = true
      const params: CompanyListParamModel = {
        name: null,
        companyCode: null
      }
      const data = await getCompanyAllList(params)
      const list = data?.data ?? []
      const optionList = []
      for (const iterator of list) {
        optionList.push({
          label: iterator.companyCode + ', ' + iterator.name,
          value: iterator.companyCode
        })
      }
      dataMap.searchFormStruct[0].options = optionList
      dataMap.searchFormStruct[0].loading = false
    }
    onMounted(() => {
      dataMap.getPageList(null, 10)
      getCompanyList()
    })
    return {
      t,
      ReportDisplayTypes,
      tableRef,
      tableRowClassName,
      groupTableRowClassName,
      ...toRefs(dataMap)
    }
  }
})
